import { useState, useMemo } from 'react'

import { ErrorItem, ErrorType, ErrorsList } from 'types/entities'

import { Button, DragAndDrop, Slash, EditorComponent, ButtonLoadFile, Notification } from 'components/ui'

import { useOpenModal } from 'modal'
import { newExampleModal } from 'modals'

import { AddressesGenerator } from 'components/features/AddressesGenerator'
import { WrapperEditor, Top, WrapEditor, Label, ButtonWrap, ErrorsWrap } from './Style'

type Props = {
  editorData?: string
  editorErrorList: ErrorsList
  duplicateErrorList: ErrorsList
  onChange: (value: string) => void
}

const btnText = {
  FILE: 'upload-file',
  MANUALLY: 'insert-manually',
}

const typeOfFiles = {
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
}

export function RecipientsEditor({ onChange, editorErrorList, duplicateErrorList, editorData = '' }: Props) {
  const openExampleModal = useOpenModal()

  const [isUploadFileArea, setIsUploadFileArea] = useState(false)

  const handleChange = (value: string) => {
    onChange(value)
  }

  const onShowExample = () => {
    openExampleModal(newExampleModal())
  }

  const handleLoadFile = async (currentFile: File) => {
    const _value = await currentFile.text()
    handleChange(_value)
    setIsUploadFileArea(false)
  }

  const onSwitchFileArea = () => {
    setIsUploadFileArea(!isUploadFileArea)
  }

  const uploadButtonText = useMemo(() => {
    return isUploadFileArea ? btnText.MANUALLY : btnText.FILE
  }, [isUploadFileArea])

  const listErrors = useMemo(() => editorErrorList.concat(duplicateErrorList), [duplicateErrorList, editorErrorList])

  const isError = useMemo(
    () => Boolean(editorErrorList.length || duplicateErrorList.length),
    [editorErrorList, duplicateErrorList],
  )

  const groupedErrors = useMemo(
    () =>
      listErrors.reduce<Record<keyof typeof ErrorType, number[]>>(
        (groups: Record<ErrorType, number[]>, error: ErrorItem) => {
          if (!groups[error.type]) {
            groups[error.type] = []
          }
          groups[error.type].push(error.line + 1)
          return groups
        },
        {} as Record<keyof typeof ErrorType, number[]>,
      ),
    [listErrors],
  )

  const errorMessages = useMemo(
    () =>
      Object.entries(groupedErrors).map(([type, lines]) => {
        const formattedLines = lines.join(', ')
        return `In ${lines.length <= 1 ? 'line' : 'lines'} ${formattedLines}: ${
          listErrors.find((error) => error.type === type)?.errorMessage
        }`
      }),
    [groupedErrors, listErrors],
  )

  return (
    <>
      <WrapperEditor>
        <Top>
          <Label htmlFor='provideList'>
            <Slash />
            _provide-list-of-recipients
          </Label>

          <ButtonWrap>
            <Button type='primary' size='small' onClick={onShowExample}>
              _example
            </Button>
            <Button type='outline' size='small' onClick={onSwitchFileArea}>
              _{uploadButtonText}
            </Button>
          </ButtonWrap>
        </Top>

        {isUploadFileArea ? (
          <WrapEditor className='dragAndDrop'>
            <DragAndDrop acceptFile={typeOfFiles} transmitData={handleLoadFile} />
            <ButtonLoadFile id='upload_csv' size='default' accept='.csv,.xls,.xlsx' transmitData={handleLoadFile}>
              _{btnText.FILE}
            </ButtonLoadFile>
          </WrapEditor>
        ) : (
          <WrapEditor id='provideList'>
            <EditorComponent
              id='provideList'
              value={editorData}
              readonly={false}
              onChange={handleChange}
              editorErrorList={listErrors}
            />
          </WrapEditor>
        )}
        <AddressesGenerator setList={handleChange} />
      </WrapperEditor>
      {isError && (
        <ErrorsWrap>
          {errorMessages.map((message, index) => (
            <Notification key={index} type='error' message={message} />
          ))}
        </ErrorsWrap>
      )}
    </>
  )
}
